import { Box, Card, CardContent, Grid, Skeleton, Typography } from '@mui/material';
import React from 'react';
import { getProfileStats, getProfileRatios } from '../../../services/profile.js';
import type { WidgetProps } from '../types/index.js';

export const KeyStatsWidget: React.FC<WidgetProps> = ({ widget }) => {
  const profileId = (widget.config as any)?.profileId as string | undefined;
  const ticker = (widget.inputs.find(i => i.id === 'ticker')?.value as string) || (widget.config as any)?.ticker;
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [stats, setStats] = React.useState<{
    marketCap: number;
    pe: number | null;
    ps: number | null;
    currentPrice: number;
    range52w: { low: number; high: number };
  } | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const id = profileId || ticker;
        if (!id) {
          setStats(null);
          setLoading(false);
          return;
        }

        // Fetch both stats and ratios in parallel
        const [statsData, ratiosData] = await Promise.all([
          getProfileStats(id).catch(() => null),
          getProfileRatios(id).catch(() => null),
        ]);

        if (cancelled) return;

        if (statsData) {
          setStats({
            marketCap: statsData.marketCap,
            pe: ratiosData?.pe ?? null,
            ps: ratiosData?.ps ?? null,
            currentPrice: statsData.currentPrice,
            range52w: statsData.range52w,
          });
        } else {
          setStats(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load key stats:', err);
          setError(err instanceof Error ? err.message : 'Failed to load stats');
          setStats(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [profileId, ticker]);

  if (loading) {
    return (
      <Grid container spacing={1} sx={{ p: 1 }}>
        {[...Array(4)].map((_, i) => (
          <Grid item xs={6} key={i}>
            <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No stats available
        </Typography>
      </Box>
    );
  }

  const formatRatio = (value: number | null): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return 'N/A';
    return value.toFixed(1);
  };

  const cards = [
    { label: 'Market Cap', value: `$${(stats.marketCap / 1e9).toFixed(0)}B` },
    { label: 'P/E', value: formatRatio(stats.pe) },
    { label: 'P/S', value: formatRatio(stats.ps) },
    { label: '52W Range', value: `${stats.range52w.low.toFixed(1)} - ${stats.range52w.high.toFixed(1)}` },
  ];

  return (
    <Grid container spacing={1} sx={{ p: 1 }}>
      {cards.map((c) => (
        <Grid item xs={6} key={c.label}>
          <Card variant="outlined">
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">{c.label}</Typography>
              <Typography variant="subtitle1">{c.value}</Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">Current:</Typography>
          <Typography variant="body2">${stats.currentPrice > 0 ? stats.currentPrice.toFixed(2) : 'N/A'}</Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

